import { supabase } from "../supabase-client";
import type { Database } from "../types/database";

export type OnboardingProgress =
  Database["public"]["Tables"]["onboarding_progress"]["Row"];
export type OnboardingProgressInsert =
  Database["public"]["Tables"]["onboarding_progress"]["Insert"];
export type OnboardingProgressUpdate =
  Database["public"]["Tables"]["onboarding_progress"]["Update"];

/**
 * Create onboarding progress
 */
export async function createOnboardingProgress(
  userId: string,
  totalSteps: number,
  metadata?: {
    ip_address?: string;
    user_agent?: string;
    referral_source?: string;
  }
) {
  // Check if progress already exists
  const existing = await supabase
    .from("onboarding_progress")
    .select("id")
    .eq("user_id", userId)
    .maybeSingle();

  if (existing.data) {
    // Update existing instead of creating duplicate
    const { data, error } = await supabase
      .from("onboarding_progress")
      .update({
        current_step: 1,
        total_steps: totalSteps,
        completed_steps: [],
        onboarding_data: {},
        ...metadata,
      })
      .eq("id", existing.data.id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  // Create new progress
  const { data, error } = await supabase
    .from("onboarding_progress")
    .insert({
      user_id: userId,
      current_step: 1,
      total_steps: totalSteps,
      completed_steps: [],
      onboarding_data: {},
      ...metadata,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Get onboarding progress
 */
export async function getOnboardingProgress(userId: string) {
  const { data, error } = await supabase
    .from("onboarding_progress")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return data;
}

/**
 * Update onboarding step
 */
export async function updateOnboardingStep(
  userId: string,
  step: number,
  stepData: Record<string, any>
) {
  // Get current progress (get most recent if duplicates exist)
  const { data: current, error: fetchError } = await supabase
    .from("onboarding_progress")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  // If no progress exists, create it
  if (!current || fetchError) {
    // Get user type from profile to determine total steps
    const { data: profile } = await supabase
      .from("profiles")
      .select("user_type")
      .eq("id", userId)
      .maybeSingle();

    const totalSteps = profile?.user_type === "lawyer" ? 3 : 4;

    // Try to insert, but if it fails due to unique constraint, get the existing one
    const { data: newProgress, error: createError } = await supabase
      .from("onboarding_progress")
      .insert({
        user_id: userId,
        current_step: step,
        total_steps: totalSteps,
        completed_steps: [step],
        onboarding_data: { [`step${step}`]: stepData },
      })
      .select()
      .maybeSingle();

    // If insert fails due to duplicate, get the existing record
    if (createError) {
      if (createError.code === "23505") {
        // Unique violation
        const { data: existing } = await supabase
          .from("onboarding_progress")
          .select("*")
          .eq("user_id", userId)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (existing) {
          // Update the existing record instead
          const completedSteps = Array.from(
            new Set([...((existing.completed_steps as number[]) || []), step])
          );
          const onboardingData = {
            ...((existing.onboarding_data as Record<string, any>) || {}),
            [`step${step}`]: stepData,
          };

          const { data: updated, error: updateError } = await supabase
            .from("onboarding_progress")
            .update({
              current_step: step,
              completed_steps: completedSteps,
              onboarding_data: onboardingData,
            })
            .eq("id", existing.id)
            .select()
            .single();

          if (updateError) throw updateError;
          return updated;
        }
      }
      throw createError;
    }

    if (!newProgress) {
      throw new Error("Failed to create onboarding progress");
    }
    return newProgress;
  }

  // Update completed steps
  const completedSteps = Array.from(
    new Set([...((current.completed_steps as number[]) || []), step])
  );

  // Merge step data
  const onboardingData = {
    ...((current.onboarding_data as Record<string, any>) || {}),
    [`step${step}`]: stepData,
  };

  const { data, error } = await supabase
    .from("onboarding_progress")
    .update({
      current_step: step,
      completed_steps: completedSteps,
      onboarding_data: onboardingData,
    })
    .eq("id", current.id) // Use the specific ID to avoid updating duplicates
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Complete onboarding
 */
export async function completeOnboarding(userId: string) {
  // Get the most recent progress entry
  const { data: progress } = await supabase
    .from("onboarding_progress")
    .select("id")
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (!progress) {
    throw new Error("Onboarding progress not found");
  }

  const { data, error } = await supabase
    .from("onboarding_progress")
    .update({
      is_completed: true,
      completed_at: new Date().toISOString(),
    })
    .eq("id", progress.id) // Use specific ID to avoid updating duplicates
    .select()
    .single();

  if (error) throw error;

  // Mark profile as onboarding completed
  await supabase
    .from("profiles")
    .update({ onboarding_completed: true })
    .eq("id", userId);

  // Fallback: ensure trial is created (trigger should do it, but call RPC if not).
  // For lawyers, retry and verify existence to avoid race conditions.
  const { data: profileData } = await supabase
    .from("profiles")
    .select("user_type")
    .eq("id", userId)
    .maybeSingle();

  const { data: existingAnyStatus } = await supabase
    .from("subscriptions")
    .select("id")
    .eq("user_id", userId)
    .limit(1)
    .maybeSingle();

  // Avoid duplicate inserts from ensure_trial when a row already exists
  if (existingAnyStatus) {
    return data;
  }

  if (profileData?.user_type === "lawyer") {
    for (let attempt = 0; attempt < 3; attempt++) {
      const { error: ensureError } = await supabase.rpc(
        "ensure_trial_for_current_user",
      );
      if (ensureError) {
        console.warn("[onboarding] ensure_trial_for_current_user:", ensureError.message);
      }

      const { data: activeSub } = await supabase
        .from("subscriptions")
        .select("id")
        .eq("user_id", userId)
        .in("status", ["active", "trialing"])
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (activeSub) break;
      await new Promise((resolve) => setTimeout(resolve, 150));
    }
  } else {
    await supabase.rpc("ensure_trial_for_current_user").then(({ error }) => {
      if (error) {
        console.warn("[onboarding] ensure_trial_for_current_user:", error.message);
      }
    });
  }

  return data;
}

/**
 * Best-effort sync of a user contact to Brevo.
 * This is non-blocking for the UX and should not break onboarding flow.
 */
export async function syncUserToBrevo(userId: string): Promise<void> {
  try {
    const { error } = await supabase.functions.invoke("sync-brevo-contact", {
      method: "POST",
      body: { userId },
    });

    if (error) {
      console.warn("[onboarding] sync-brevo-contact:", error.message);
    }
  } catch (error) {
    console.warn("[onboarding] sync-brevo-contact failed:", error);
  }
}

/**
 * Check if onboarding is completed
 */
export async function isOnboardingCompleted(userId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from("onboarding_progress")
    .select("is_completed")
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error || !data) return false;
  return data?.is_completed || false;
}

/**
 * Get onboarding completion percentage
 */
export async function getOnboardingCompletionPercentage(
  userId: string
): Promise<number> {
  const { data, error } = await supabase
    .from("onboarding_progress")
    .select("completed_steps, total_steps")
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error || !data) return 0;

  const completedCount = ((data.completed_steps as number[]) || []).length;
  const totalSteps = data.total_steps || 1;

  return Math.round((completedCount / totalSteps) * 100);
}

/**
 * Reset onboarding (admin only)
 */
export async function resetOnboarding(userId: string) {
  // Get the most recent progress entry
  const { data: progress } = await supabase
    .from("onboarding_progress")
    .select("id")
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (!progress) {
    throw new Error("Onboarding progress not found");
  }

  const { data, error } = await supabase
    .from("onboarding_progress")
    .update({
      current_step: 1,
      completed_steps: [],
      is_completed: false,
      completed_at: null,
      onboarding_data: {},
    })
    .eq("id", progress.id) // Use specific ID to avoid updating duplicates
    .select()
    .single();

  if (error) throw error;

  // Reset profile onboarding status
  await supabase
    .from("profiles")
    .update({ onboarding_completed: false })
    .eq("id", userId);

  return data;
}

/**
 * Delete onboarding progress
 */
export async function deleteOnboardingProgress(userId: string) {
  const { error } = await supabase
    .from("onboarding_progress")
    .delete()
    .eq("user_id", userId);

  if (error) throw error;
}

/**
 * Get all onboarding data for user
 */
export async function getOnboardingData(userId: string) {
  const { data, error } = await supabase
    .from("onboarding_progress")
    .select("onboarding_data")
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return (data?.onboarding_data as Record<string, any>) || {};
}
